<template>
  <v-card :loading="loading || saving">
    <v-card-title class="float-left">
      Toutes nos offres
    </v-card-title>
    <LocalSwitcher @updateCurrentLocale="currentLocale = $event"></LocalSwitcher>
    <v-card-text>
      <v-alert
        v-for="(error, index) in errors"
        :key="index"
        type="error"
        text
      >
        {{ error }}
      </v-alert>
      <v-btn
        v-if="!ready && !loading"
        color="primary"
        @click="load"
      >
        Réessayer
      </v-btn>
      <v-alert
        v-if="saved"
        type="success"
        text
        dismissible
      >
        La page a été enregistrée.
      </v-alert>
      <v-form
        v-if="ready"
        @submit.prevent="save"
      >
        <fieldset :disabled="saving">
          <h3 class="mt-6 mb-4">
            En-tête de la page
          </h3>
          <v-text-field
            v-model="page.translations[currentLocale].title"
            label="Titre"
            outlined
            maxlength="255"
            :disabled="saving"
          >
            <template #append>
              <country-flag
                :country="currentLocale === 'en' ? 'gb' : currentLocale"
                size="normal"
              />
            </template>
          </v-text-field>
          <label class="d-block mb-2">Description</label>
          <ckeditor
            :key="'description-' + currentLocale"
            v-model="page.translations[currentLocale].description"
            :editor="editor"
            :config="editorConfig"
            :disabled="saving"
          />

          <h3 class="mt-6 mb-4">
            Promotions de la mosaïque
          </h3>
          <v-autocomplete
            v-model="page.superCategoryIds"
            :items="categoryOptions"
            label="Offres spéciales à afficher"
            multiple
            chips
            deletable-chips
            outlined
            :disabled="saving"
            hint="Seules les offres activées et dans leur période de publication sont affichées sur le site."
            persistent-hint
          ></v-autocomplete>
          <p class="mt-4">
            Glissez les offres pour choisir leur ordre, ou utilisez les flèches.
            Sur desktop : 2/3, 1/3, puis pleine largeur. Sur tablette : 1/2, 1/2, puis pleine largeur.
            Sur smartphone : une image carrée par ligne.
          </p>
          <draggable
            v-model="page.superCategoryIds"
            handle=".promotion-drag-handle"
            :disabled="saving"
            tag="ol"
            class="promotion-selection"
          >
            <li
              v-for="(id, index) in page.superCategoryIds"
              :key="id"
              class="promotion-selection-item"
            >
              <span
                class="promotion-drag-handle"
                aria-hidden="true"
              >⠿</span>
              <div class="promotion-selection-label">
                <strong>{{ index + 1 }}. {{ categoryName(id) }}</strong>
                <div>{{ formatLabel(index) }}</div>
              </div>
              <div class="promotion-selection-actions">
                <v-btn
                  icon
                  :disabled="saving || index === 0"
                  :aria-label="'Monter ' + categoryName(id)"
                  @click="move(index, -1)"
                >
                  <v-icon>{{ icons.mdiArrowUp }}</v-icon>
                </v-btn>
                <v-btn
                  icon
                  :disabled="saving || index === page.superCategoryIds.length - 1"
                  :aria-label="'Descendre ' + categoryName(id)"
                  @click="move(index, 1)"
                >
                  <v-icon>{{ icons.mdiArrowDown }}</v-icon>
                </v-btn>
                <v-btn
                  small
                  text
                  color="primary"
                  :to="{ name: 'specialOfferDetails', params: { specialOfferId: id }, query: { tab: 'images' } }"
                  target="_blank"
                  rel="noopener"
                >
                  Gérer les images
                </v-btn>
                <v-btn
                  icon
                  :disabled="saving"
                  :aria-label="'Retirer ' + categoryName(id)"
                  @click="page.superCategoryIds.splice(index, 1)"
                >
                  <v-icon>{{ icons.mdiClose }}</v-icon>
                </v-btn>
              </div>
            </li>
          </draggable>
          <p v-if="!page.superCategoryIds.length">
            Aucune offre sélectionnée.
          </p>

          <h3 class="mt-6 mb-4">
            Bas de page
          </h3>
          <label class="d-block mb-2">Description de bas de page</label>
          <ckeditor
            :key="'bottom-description-' + currentLocale"
            v-model="page.translations[currentLocale].bottomDescription"
            :editor="editor"
            :config="editorConfig"
            :disabled="saving"
          />
          <v-btn
            type="submit"
            color="primary"
            class="mt-6"
            :loading="saving"
            :disabled="saving"
          >
            Enregistrer la page
          </v-btn>
        </fieldset>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue2'
import FullEditor from 'ckeditor5-custom-build'
import draggable from 'vuedraggable'
import { mdiArrowUp, mdiArrowDown, mdiClose } from '@mdi/js'
import config from '@/config'
import LocalSwitcher from '@/components/Common/LocalSwitcher.vue'

const locales = ['fr', 'en', 'nl', 'de']

export default {
  components: { LocalSwitcher, draggable, ckeditor: CKEditor.component },
  data () {
    return {
      currentLocale: 'fr',
      loading: false,
      saving: false,
      ready: false,
      saved: false,
      errors: [],
      categories: [],
      page: { superCategoryIds: [], translations: {} },
      icons: { mdiArrowUp, mdiArrowDown, mdiClose },
      editor: FullEditor,
      editorConfig: {
        toolbar: { shouldNotGroupWhenFull: true },
        htmlSupport: { allow: [{ name: /.*/, attributes: true, classes: true, styles: true }] },
        simpleUpload: {
          uploadUrl: config.apiUrl + '/api/medias',
          headers: { Authorization: 'Bearer ' + this.$store.getters.getToken }
        }
      }
    }
  },
  computed: {
    categoryOptions () {
      return this.categories.map(category => ({ value: category.id, text: this.categoryName(category.id) }))
    }
  },
  created () {
    this.load()
  },
  methods: {
    requestOptions () {
      return { headers: { Authorization: 'Bearer ' + this.$store.getters.getToken } }
    },
    normalizePage (data) {
      const translations = {}
      locales.forEach(locale => {
        const translation = (data.translations || {})[locale] || {}
        translations[locale] = {
          title: translation.title || '',
          description: translation.description || '',
          bottomDescription: translation.bottomDescription || ''
        }
      })

      return {
        superCategoryIds: [...(data.superCategoryIds || [])],
        translations
      }
    },
    async load () {
      this.loading = true
      this.errors = []
      try {
        const [page, categories] = await Promise.all([
          this.$http.get(config.apiUrl + '/api/special-offer/promotions-page', this.requestOptions()),
          this.$http.get(config.apiUrl + '/api/special-offer/super-categories', this.requestOptions())
        ])
        this.page = this.normalizePage(page.data)
        this.categories = categories.data
        this.ready = true
      } catch (error) {
        this.errors = this.errorMessages(error, 'Impossible de charger la page. Veuillez réessayer.')
      } finally {
        this.loading = false
      }
    },
    async save () {
      if (this.saving || !this.ready) return

      this.saving = true
      this.saved = false
      this.errors = []
      try {
        const response = await this.$http.put(
          config.apiUrl + '/api/special-offer/promotions-page', this.page, this.requestOptions()
        )
        this.page = this.normalizePage(response.data)
        this.saved = true
      } catch (error) {
        this.errors = this.errorMessages(error, 'Impossible d’enregistrer la page. Vos modifications sont conservées dans le formulaire.')
      } finally {
        this.saving = false
      }
    },
    errorMessages (error, fallback) {
      const data = error.response && error.response.data
      if (data && Array.isArray(data.violations)) {
        return data.violations.map(violation => violation.message || violation.title || fallback)
      }

      return [data && (data.message || data.error) || fallback]
    },
    categoryName (id) {
      const category = this.categories.find(item => item.id === id)
      if (!category) return 'Offre indisponible'

      const translations = category.translations || {}
      const translation = translations[this.currentLocale] || translations.fr || {}

      return translation.name || (translations.fr || {}).name || 'Offre sans titre'
    },
    formatLabel (index) {
      return ['Desktop 2/3 · Tablette 1/2', 'Desktop 1/3 · Tablette 1/2', 'Desktop et tablette : pleine largeur'][index % 3]
    },
    move (index, direction) {
      const destination = index + direction
      if (destination < 0 || destination >= this.page.superCategoryIds.length) return

      const ids = [...this.page.superCategoryIds]
      const [id] = ids.splice(index, 1)
      ids.splice(destination, 0, id)
      this.page.superCategoryIds = ids
    }
  }
}
</script>

<style scoped>
fieldset {
  border: 0;
  min-width: 0;
}
.promotion-selection {
  padding: 0;
  list-style: none;
}
.promotion-selection-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #ddd;
}
.promotion-drag-handle {
  cursor: grab;
  font-size: 26px;
}
.promotion-selection-label {
  flex: 1;
  min-width: 160px;
}
.promotion-selection-actions {
  display: flex;
  align-items: center;
}
</style>
